import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { auditPromptsQuery, savePrompts, type AuditPrompts, type PromptBlock } from '../../api/auditPrompts'
import { ApiError } from '../../api/client'
import { AdmPageHead } from '../../softsee/adm/AdmPageHead'
import { Forbidden } from '../../ui/Forbidden'
import { Content } from '../../ui/Shell'
import { Spinner } from '../../ui/Spinner'
import { usePageStyles } from '../../ui/usePageStyles'

/**
 * `settings/Audit::prompts` - briefingul agentului, pe bucati, la
 * /app/settings/audit/prompts/$type (0 = toate auditurile). Macheta:
 * `settings/audit/prompts.php`.
 *
 * O caseta pe regula, cu motivul ei scris alaturi. Datele manualului -
 * verificarile, treptele, notele - NU sunt aici: se calculeaza, si nu se pot
 * rescrie dintr-o caseta, altfel un audit ar putea fi trecut prin editarea
 * propriului barem. O caseta lasata exact ca implicitul nu se stocheaza.
 */
const STYLES = ['css/adm.css', 'css/audit.css']

export function AuditPromptsPage({ type }: { type: number }) {
  const page = useQuery(auditPromptsQuery(type))
  usePageStyles(STYLES, page.data?.version ?? '')

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div id="content" className="od-audit">
          <p>
            No such audit type. <Link to="/settings/audit/prompts/$type" params={{ type: '0' }}>All audits</Link>.
          </p>
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  return <PromptsForm key={`${type}:${page.data.saved}`} type={type} data={page.data} />
}

function PromptsForm({ type, data }: { type: number; data: AuditPrompts }) {
  const client = useQueryClient()
  const [bodies, setBodies] = useState<Record<string, string>>(() =>
    Object.fromEntries(data.blocks.map((b) => [b.key, b.live])),
  )
  const [saved, setSaved] = useState(data.saved)
  const save = useMutation({
    mutationFn: () => savePrompts(type, bodies),
    onSuccess: (fresh) => {
      client.setQueryData(auditPromptsQuery(type).queryKey, fresh)
      setSaved(fresh.saved)
    },
  })

  /* Cate casete s-au schimbat si nu s-au salvat: douazeci si sase de casete
     cu un singur buton, fara numarul asta nu stii daca butonul are ce salva. */
  const dirty = useMemo(
    () => data.blocks.filter((b) => (bodies[b.key] ?? '') !== b.live).length,
    [bodies, data.blocks],
  )

  useEffect(() => {
    if (dirty === 0) return
    const guard = (e: BeforeUnloadEvent) => {
      e.preventDefault()
    }
    window.addEventListener('beforeunload', guard)
    return () => window.removeEventListener('beforeunload', guard)
  }, [dirty])

  const mine = Object.values(data.changed).filter((w) => w === 'this audit').length
  const changedCount = Object.keys(data.changed).length
  const lostKeys = Object.keys(data.lost ?? {})
  const behindKeys = Object.keys(data.behind ?? {})

  return (
    <Content>
      <div id="content" className="od-audit od-audit-prompts">
        <AdmPageHead
          tone="blue-mauve"
          icon="fa-comment-dots"
          title="The briefing"
          note="Everything the agent is told during an audit, one rule per box. Clear a box to go back to the default. What the manual measures - the checks, the ladder, the ratings - is computed and is not editable here."
          actions={
            <a className="btn btn-default" href="/app/settings/audit">
              Back to the catalogue
            </a>
          }
          stats={[
            { value: String(data.blocks.length), label: 'blocks', tone: 'blue-mauve' },
            { value: String(changedCount), label: 'rewritten in force', tone: 'blue-mauve' },
            { value: String(mine), label: type ? 'only for this audit' : 'for all audits', tone: 'blue-mauve' },
            {
              value: data.fingerprint,
              label: data.fingerprint === data.stock ? 'as it ships' : 'rewritten - runs carry this',
              tone: 'blue-mauve',
            },
          ]}
        />

        {/* Rescrisurile ramase in urma, spuse inainte de orice - prompts.php.
            Un rescris care a scos `{asks}`/`{tools}` a INGHETAT lista pe care
            o purtau, in forma de atunci. */}
        {(lostKeys.length > 0 || behindKeys.length > 0) && (
          <div className="adm-card od-audit-behind">
            <h4>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" />
              Some rewritten blocks have fallen behind
            </h4>
            {lostKeys.length > 0 && (
              <>
                <p>
                  <strong>
                    {lostKeys.length} {lostKeys.length === 1 ? 'block has' : 'blocks have'} dropped a placeholder.
                  </strong>{' '}
                  A placeholder is filled in when the audit runs, from the manual and from the tools the agent may use - the part that is
                  computed and not edited here. A rewrite without it does not lose a word: it freezes the list the placeholder carried, as
                  that list stood on the day of the rewrite. That is where questions come from that moved into the computed part long ago.
                </p>
                <ul className="od-audit-behind-list">
                  {lostKeys.map((key) => (
                    <li key={key}>
                      <code>{key}</code> &mdash; lost{' '}
                      {(data.lost[key] ?? []).map((one) => (
                        <code key={one}>{`{${one}}`}</code>
                      ))}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {behindKeys.length > 0 && (
              <>
                <p>
                  <strong>
                    {behindKeys.length} {behindKeys.length === 1 ? 'block was' : 'blocks were'} written against an older default.
                  </strong>{' '}
                  The default has changed since - or, for a rewrite older than this screen, it cannot be known which default it was written
                  against, and &quot;cannot be known&quot; is not the same as &quot;fine&quot;. Put the default back to see what it says now;
                  your text is on screen until you save, so nothing is lost by looking.
                </p>
                <ul className="od-audit-behind-list">
                  {behindKeys.map((key) => (
                    <li key={key}>
                      <code>{key}</code> &mdash;{' '}
                      {data.behind[key] === 'moved'
                        ? 'the default moved on after this was written'
                        : 'written before we recorded which default it was based on'}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {saved > 0 && (
          <div className="adm-card od-audit-saved">
            <i className="fas fa-check" aria-hidden="true" /> Saved. The next run of {type && data.type ? data.type.title : 'every audit'} is
            briefed with this.
          </div>
        )}

        {!data.ready && (
          <div className="adm-card">
            <strong>The prompt table is not on this instance yet.</strong>{' '}
            <span className="adm-muted">
              Change Log &rsaquo; Re-apply schema creates it. Until then every block runs on its default, which is what you see below.
            </span>
          </div>
        )}

        <div className="adm-card od-audit-scope-pick">
          <span className="adm-muted">Editing:</span>
          <Link className={`od-audit-chip${type === 0 ? ' is-on' : ''}`} to="/settings/audit/prompts/$type" params={{ type: '0' }}>
            All audits
          </Link>
          {data.types.map((one) => (
            <Link
              key={one.id}
              className={`od-audit-chip${type === one.id ? ' is-on' : ''}`}
              to="/settings/audit/prompts/$type"
              params={{ type: String(one.id) }}
            >
              {one.title}
            </Link>
          ))}
          <p className="adm-muted od-audit-scope-why">
            A block written for one audit beats the one written for all audits. Nothing written here changes an audit that has already
            run: a snapshot keeps the words it was made with.
          </p>
        </div>

        {data.trail.length > 0 && (
          <>
            <div className="adm-section">
              <h4>Who changed what</h4>
              <span className="adm-section-note">
                {data.trail.length} rewrite{data.trail.length === 1 ? '' : 's'} in force
              </span>
            </div>
            <div className="adm-card od-audit-trail">
              <table className="table od-audit-table">
                <thead>
                  <tr>
                    <th>Block</th>
                    <th>Where</th>
                    <th>Who</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trail.map((one, i) => (
                    <tr key={`${one.key}:${i}`}>
                      <td>{one.title}</td>
                      <td>{one.where}</td>
                      <td>{one.who}</td>
                      <td className="adm-muted">{one.changed_at}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          {data.phases.map((phase) => (
            <div key={phase.key}>
              <div className="adm-section">
                <h4>{phase.title}</h4>
                <span className="adm-section-note">
                  {phase.when} &middot; {phase.count} blocks
                </span>
              </div>
              {data.blocks
                .filter((b) => b.phase === phase.key)
                .map((b) => (
                  <BlockCard
                    key={b.key}
                    block={b}
                    body={bodies[b.key] ?? ''}
                    changed={data.changed[b.key]}
                    lost={data.lost?.[b.key]}
                    behind={data.behind?.[b.key]}
                    onChange={(text) => setBodies((prev) => ({ ...prev, [b.key]: text }))}
                  />
                ))}
            </div>
          ))}

          <div className="adm-card od-audit-prompts-save" id="promptsSave">
            <button type="submit" className="btn btn-primary" disabled={!data.ready || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save the briefing'}
            </button>
            <span className="od-audit-dirty" id="promptsDirty" hidden={dirty === 0}>
              {dirty === 1 ? '1 block changed, not saved' : `${dirty} blocks changed, not saved`}
            </span>
            <span className="adm-muted">
              A box left exactly as the default is not stored, so the default can still improve in a later release without being frozen
              by an edit that changed nothing.
            </span>
            {save.error && <span className="text-danger"> {save.error instanceof ApiError ? save.error.message : 'That did not save.'}</span>}
          </div>
        </form>
      </div>
    </Content>
  )
}

function BlockCard({
  block,
  body,
  changed,
  lost,
  behind,
  onChange,
}: {
  block: PromptBlock
  body: string
  changed: string | undefined
  lost: string[] | undefined
  behind: string | undefined
  onChange: (text: string) => void
}) {
  const isDefault = body.trim() === block.text.trim()
  const holes = Array.from(new Set(Array.from(block.text.matchAll(/\{([a-z]+)\}/g), (m) => m[1] ?? '')))
  const rows = Math.max(3, Math.min(12, Math.ceil(body.length / 95)))

  return (
    <article className={`adm-card od-audit-block${isDefault ? '' : ' is-rewritten'}${body !== block.live ? ' is-dirty' : ''}`}>
      <header className="od-audit-block-head">
        <span className="od-audit-block-title">{block.title}</span>
        {changed !== undefined && <span className="adm-pill adm-pill-warn">rewritten &middot; {changed}</span>}
        {lost !== undefined ? (
          <span className="adm-pill adm-pill-bad">froze {lost.map((one) => `{${one}}`).join('')}</span>
        ) : (
          behind !== undefined && (
            <span className="adm-pill adm-pill-warn">{behind === 'moved' ? 'the default moved on' : 'based on an unknown default'}</span>
          )
        )}
        <code className="od-audit-block-key">{block.key}</code>
      </header>
      <p className="od-audit-block-why">{block.why}</p>
      <textarea
        className="form-control od-audit-block-box"
        name={`block[${block.key}]`}
        rows={rows}
        value={body}
        onChange={(e) => onChange(e.target.value)}
      />
      <footer className="od-audit-block-foot">
        {holes.length > 0 && (
          <span className="adm-muted">
            Fills in: {holes.map((h) => <code key={h}>{`{${h}}`} </code>)} &mdash; drop it and the list it carries goes with it.
          </span>
        )}
        {!isDefault && (
          <button type="button" className="btn btn-link od-audit-block-reset" onClick={() => onChange(block.text)}>
            Put the default back
          </button>
        )}
      </footer>
    </article>
  )
}
